import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { formatISO, isWithinInterval, parseISO } from "date-fns";
import { v4 as uuidV4 } from "uuid";
import {
  AlertRequest,
  ConfirmationRequest,
  Venue,
  WOLAlert,
  WOLConfirmation,
} from "../../app/types";
import now from "../../util/now";
import api from "../api/api";

interface UIState {
  rightMenuIsOpen: boolean;
  scheduleDate: string;
  audioSources: OT.Device[];
  videoSources: OT.Device[];
  selectedAudioSource?: OT.Device;
  selectedVideoSource?: OT.Device;
  alerts: WOLAlert[];
  confirmations: WOLConfirmation[];
}

const initialState: UIState = {
  rightMenuIsOpen: true,
  scheduleDate: formatISO(now),
  audioSources: [],
  videoSources: [],
  alerts: [],
  confirmations: [],
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    addConfirmation: {
      prepare: (confirmationRequest: ConfirmationRequest) => ({
        payload: { ...confirmationRequest, uuid: uuidV4() },
      }),
      reducer: (state, action: PayloadAction<WOLConfirmation>) => {
        state.confirmations.push(action.payload);
      },
    },
    confirmationResult: {
      prepare: (uuid: string, accepted: boolean) => ({
        payload: { uuid, accepted },
      }),
      reducer: (
        state,
        action: PayloadAction<{ uuid: string; accepted: boolean }>,
      ) => {
        const { uuid, accepted } = action.payload;
        const index = state.confirmations.findIndex(
          (item) => item.uuid === uuid,
        );
        if (index !== -1) {
          state.confirmations[index].userAccepted = accepted;
        }
      },
    },
    removeConfirmation: {
      prepare: (uuid: string) => ({
        payload: { uuid },
      }),
      reducer: (state, action: PayloadAction<{ uuid: string }>) => {
        const { uuid } = action.payload;
        state.confirmations = state.confirmations.filter(
          (confirmation) => confirmation.uuid !== uuid,
        );
      },
    },
    addAlert: {
      prepare: (alertRequest: AlertRequest) => ({
        payload: { ...alertRequest, uuid: uuidV4() },
      }),
      reducer: (state, action: PayloadAction<WOLAlert>) => {
        const { msg, uuid, title } = action.payload;
        state.alerts.push({ msg, uuid, title });
      },
    },
    removeAlert: {
      prepare: (uuid: string) => ({
        payload: { uuid },
      }),
      reducer: (state, action: PayloadAction<{ uuid: string }>) => {
        const { uuid } = action.payload;
        state.alerts = state.alerts.filter((alert) => alert.uuid !== uuid);
      },
    },
    selectSource: {
      prepare: (source: OT.Device) => ({
        payload: { source },
      }),
      reducer: (state, action: PayloadAction<{ source: OT.Device }>) => {
        const { source } = action.payload;
        if (source.kind === "videoInput") {
          state.selectedVideoSource = source;
        }
        if (source.kind === "audioInput") {
          state.selectedAudioSource = source;
        }
      },
    },
    setSources: {
      prepare: (sources: OT.Device[]) => ({
        payload: { sources },
      }),
      reducer: (state, action: PayloadAction<{ sources: OT.Device[] }>) => {
        const { sources } = action.payload;
        const videoSources: OT.Device[] = [];
        const audioSources: OT.Device[] = [];
        sources.forEach((source) => {
          if (source.kind === "audioInput") {
            audioSources.push(source);
          } else if (source.kind === "videoInput") {
            videoSources.push(source);
          }
        });
        state.audioSources = audioSources;
        state.videoSources = videoSources;

        if (state.selectedAudioSource === undefined) {
          state.selectedAudioSource = audioSources[0];
          state.selectedVideoSource = videoSources[0];
        }
      },
    },
    setScheduleDate: {
      prepare: (scheduleDate: string) => ({
        payload: { scheduleDate },
      }),
      reducer: (state, action: PayloadAction<{ scheduleDate: string }>) => {
        state.scheduleDate = action.payload.scheduleDate;
      },
    },
    toggleRightMenu(state) {
      state.rightMenuIsOpen = !state.rightMenuIsOpen;
    },
    setRightMenu: {
      prepare: (rightMenuIsOpen: boolean) => ({
        payload: { rightMenuIsOpen },
      }),
      reducer: (state, action: PayloadAction<{ rightMenuIsOpen: boolean }>) => {
        state.rightMenuIsOpen = action.payload.rightMenuIsOpen;
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      api.endpoints.getVenue.matchFulfilled,
      (state, action: PayloadAction<Venue>) => {
        let { scheduleDate } = state;
        const venue = action.payload;
        const end = parseISO(venue.eventEndTime);
        const start = parseISO(venue.eventStartTime);
        const interval = { start, end };
        if (!isWithinInterval(parseISO(scheduleDate), interval)) {
          scheduleDate = venue.eventStartTime;
        }
        state.scheduleDate = scheduleDate;
      },
    );
  },
});

export default uiSlice;
export const {
  toggleRightMenu,
  setRightMenu,
  setScheduleDate,
  setSources,
  selectSource,
  addAlert,
  removeAlert,
  addConfirmation,
  confirmationResult,
  removeConfirmation,
} = uiSlice.actions;
